import { QueryLazyOptions } from "@apollo/client/react/types/types";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SortingRule } from "react-table";

import { JbpOffsetPaginationDetails, Maybe } from "../main/graphql";
import useDebounce from "./useDebounce";

export type PageOptions = {
  currentPage: number;
  totalPages: number;
  handlePageNumberChange(p: number): void;
  sortBy: SortingRule<string>;
  onChangeSort(s: SortingRule<string>[]): void;
};

type JbpOffsetPaginationReturn<T> = {
  listItems: T[];
  setListItems(items: T[]): void;
  setNumberOfPages(n: number): void;
  pageOptions: PageOptions;
  setSearchTerm(q: string): void;
  setLocationId(q: string): void;
  setClientId(q: string): void;
  searchTerm: string;
  locationId: string;
  clientId: string;
  didFetchComplete: boolean;
  reload: () => void;
};

function useJbpOffsetPagination<T>(
  getData: (
    options?: QueryLazyOptions<{
      pagination?: Maybe<JbpOffsetPaginationDetails>;
    }>
  ) => any,
  options: {
    initialSortBy: SortingRule<string>;
    initialSearchTerm?: string;
    initialLocationId?: string;
    initialClientId?: string;
    ignoreURLParams?: boolean;
    pageLimit?: number;
    scrollToTop?: boolean;
  }
): JbpOffsetPaginationReturn<T> {
  const {
    initialSortBy,
    initialSearchTerm,
    initialLocationId,
    initialClientId,
    ignoreURLParams,
    pageLimit,
    scrollToTop,
  } = {
    initialSearchTerm: "",
    initialLocationId: "",
    initialClientId: "",
    ignoreURLParams: false,
    pageLimit: 40,
    scrollToTop: true,
    ...options,
  };
  const [listItems, setListItems] = useState<T[]>([]);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [didFetchComplete, setDidFetchComplete] = useState(false);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [locationId, setLocationId] = useState(initialLocationId);
  const [clientId, setClientId] = useState(initialClientId);
  const [sortBy, setSortBy] = useState<SortingRule<string>>(initialSortBy);
  const [searchParams, setSearchParams] = useSearchParams();
  const debouncedSearchTerm = useDebounce(searchTerm, 350);
  const debouncedLocation = useDebounce(locationId, 350);
  const debouncedClient = useDebounce(clientId, 350);

  useEffect(() => {
    if (!ignoreURLParams) {
      const page = searchParams.get("page");
      const sortBy = searchParams.get("sortBy");
      const desc = searchParams.get("desc");
      const searchTerm = searchParams.get("searchTerm");
      const locationId = searchParams.get("locationId");
      const clientId = searchParams.get("clientId");
      if (page) {
        setCurrentPage(Number(page));
      }
      if (sortBy) {
        setSortBy({
          id: sortBy,
          desc: desc !== "false",
        });
      }
      if (searchTerm) {
        setSearchTerm(searchTerm);
      }
      if (locationId) {
        setLocationId(locationId);
      }
      if (clientId) {
        setClientId(clientId);
      }
    }
  }, [ignoreURLParams]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, debouncedLocation, debouncedClient]);

  useEffect(() => {
    if (!ignoreURLParams) {
      const firstNavigation = !searchParams.has("page");
      searchParams.set("page", currentPage.toString());
      searchParams.set("sortBy", sortBy.id);
      searchParams.set("searchTerm", searchTerm);
      searchParams.set("clientId", clientId);
      searchParams.set("locationId", locationId);
      searchParams.set("desc", sortBy.desc ? "true" : "false");
      setSearchParams(searchParams, { replace: firstNavigation });
    }
  }, [
    currentPage,
    sortBy,
    debouncedSearchTerm,
    debouncedClient,
    debouncedLocation,
    ignoreURLParams,
  ]);

  const load = useCallback(() => {
    const sortedBy = sortBy.desc ? `${sortBy.id} desc` : `${sortBy.id} asc`;
    const limit = pageLimit || 40;
    getData({
      variables: {
        pagination: {
          limit,
          page: currentPage,
          orderBy: sortedBy,
          searchTerm: debouncedSearchTerm,
          clientId: debouncedClient,
          locationId: debouncedLocation,
        },
      },
    });
  }, [
    sortBy,
    currentPage,
    debouncedSearchTerm,
    debouncedClient,
    debouncedLocation,
    pageLimit,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  const onChangeSort = (sortBy: SortingRule<string>[]): void => {
    if (sortBy.length > 0) {
      setSortBy(sortBy[0]);
    }
  };

  const handlePageNumberChange = (pageNumber: number): void => {
    if (pageNumber) {
      setCurrentPage(pageNumber);
    }
  };

  const setListItemsAndScrollToTop = (listItems: T[]): void => {
    setDidFetchComplete(true);
    setListItems(listItems);
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const pageOptions = {
    currentPage,
    totalPages: numberOfPages,
    handlePageNumberChange,
    onChangeSort,
    sortBy,
  };
  return {
    listItems,
    setListItems: setListItemsAndScrollToTop,
    setNumberOfPages,
    pageOptions,
    setSearchTerm,
    setClientId,
    setLocationId,
    searchTerm,
    clientId,
    locationId,
    didFetchComplete,
    reload: load,
  };
}

export default useJbpOffsetPagination;
